import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useEffect } from "react";
import { AuthProvider, useAuth, peekPostLoginRedirect, clearPostLoginRedirect } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import InitiativesPage from "@/pages/InitiativesPage";
import InitiativeDetailPage from "@/pages/InitiativeDetailPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import TasksPage from "@/pages/TasksPage";
import TaskDetailPage from "@/pages/TaskDetailPage";
import IssuesPage from "@/pages/IssuesPage";
import TeamPage from "@/pages/TeamPage";
import TeamMemberDetailPage from "@/pages/TeamMemberDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import PublicReportPage from "@/pages/PublicReportPage";
import PublicDigestPage from "@/pages/PublicDigestPage";
import LeaveDecidePage from "@/pages/LeaveDecidePage";
import SkillsPage from "@/pages/SkillsPage";
import LeavesPage from "@/pages/LeavesPage";
import DigestPage from "@/pages/DigestPage";
import ChatPage from "@/pages/ChatPage";
import JoinPage from "@/pages/JoinPage";
import AccountPage from "@/pages/AccountPage";
import TwoFactorPage from "@/pages/TwoFactorPage";
import AuditLogPage from "@/pages/AuditLogPage";
import SecuritySettingsPage from "@/pages/SecuritySettingsPage";
import PortalAppsPage from "@/pages/PortalAppsPage";
import ChatWidget from "@/components/ChatWidget";
import ImportAccountDialog from "@/components/ImportAccountDialog";
import VersionWatcher from "@/components/VersionWatcher";

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="trk-uppercase-label">Loading…</div>
      </div>
    );
  }
  /*
    Remember where they were trying to go.

    This used to bounce to /login and forget, so signing in always landed on
    the Dashboard. That was survivable while every route was reached by
    clicking around inside the app — but report emails now carry a deep link
    (/reports?highlight=<id>), and a recipient who was not already signed in
    would arrive, be sent to /login, and end up on the Dashboard with no way
    back to the report they were sent. The button would have half-worked.

    Only pathname and search are kept, and LoginPage checks the pathname starts
    with "/" before using it, so this cannot be turned into an open redirect to
    another host.
  */
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search } }}
      />
    );
  }
  return children;
}

// After the Microsoft round-trip, send the user on to the deep link they were
// originally after (parked in sessionStorage by AuthContext), not just "/".
function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  useEffect(() => {
    if (user) clearPostLoginRedirect();
  }, [user]);
  if (!ready) return null;
  if (user) return <Navigate to={peekPostLoginRedirect()} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <VersionWatcher />
      <BrowserRouter>
          <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            {/* Self-registration is gone — Microsoft SSO creates the account on first sign-in. */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/join/:token" element={<JoinPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/initiatives" element={<ProtectedRoute><InitiativesPage /></ProtectedRoute>} />
            <Route path="/initiatives/:id" element={<ProtectedRoute><InitiativeDetailPage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
            <Route path="/tasks/:id" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
            <Route path="/issues" element={<ProtectedRoute><IssuesPage /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
            <Route path="/team/:id" element={<ProtectedRoute><TeamMemberDetailPage /></ProtectedRoute>} />
            <Route path="/skills" element={<ProtectedRoute><SkillsPage /></ProtectedRoute>} />
            <Route path="/leaves" element={<ProtectedRoute><LeavesPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            {/*
                A report shared by link. NEITHER ProtectedRoute NOR PublicOnly:
                it must open for somebody with no account (the reason it
                exists), and it must NOT bounce a signed-in reader to the
                dashboard the way PublicOnly would. The share token in the path
                is the only thing that authorises it.
            */}
            <Route path="/r/:token" element={<PublicReportPage />} />
            {/* A digest shared by link — identical reasoning to /r/:token, for digests. */}
            <Route path="/d/:token" element={<PublicDigestPage />} />
            {/*
                A leave-request email's Approve/Reject links. Same reasoning
                as /r/:token: must open with no sign-in, and must not bounce
                a signed-in reader who happens to already have a session in
                this browser — see LeaveDecidePage.jsx.
            */}
            <Route path="/leave-decide" element={<LeaveDecidePage />} />
            <Route path="/digest" element={<ProtectedRoute><DigestPage /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/account/password" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
            <Route path="/account/2fa" element={<ProtectedRoute><TwoFactorPage /></ProtectedRoute>} />
            <Route path="/team/audit" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
            <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
            <Route path="/settings/digital-portal" element={<ProtectedRoute><PortalAppsPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ChatWidget />
          <ImportAccountDialog />
          </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
